import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import { DndItem } from './ItemWrapper';
import layoutStore from '../stores/layoutStore';
import useStore from '../stores/store';
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import useDndStore from '../stores/dndStore';
import { AnimatePresence, motion } from 'framer-motion';
import usePersistStore from '../stores/persistStore';
import { useEffect, useRef, useState } from 'react';
import {
    Image,
    Spinner,
} from '@chakra-ui/react';
import {
    ChevronOpen,
    ChevronRightIcon,
    MotionBox,
} from './UiElements';
import EmojiPicker from './EmojiPicker';
import { useBoolean } from '@chakra-ui/hooks';
import { Resizable } from 're-resizable';
import { chakra } from '@chakra-ui/react';
import { Button, IconButton } from '@chakra-ui/button';
import GroupMenu from './GroupMenu';
import EditableTitle from './EditableTitle';
import {
    closeTabs,
    openNewGroupFromFolder
} from '../../platformFiles/browserAPI';
import DropTabNewGroup from './DropTabNewGroup';
import { slimScrollStyles } from './customTheme';
import GroupsSkeleton from './GroupsSkeleton';
import { isTrialReached } from './userApi';
import { CSS } from '@dnd-kit/utilities';
import { useItemSize } from '../Utils/useItemSize';
import { ErrorBoundary } from 'react-error-boundary';
import { GroupErrorFallback } from './ErrorAlertModal';
import { Virtuoso } from 'react-virtuoso';
import { PLATFORM } from '../../platformFiles/utils/platformConstants';
import OpenGroupButton from './OpenGroupButton';

const ResizableBox = chakra(Resizable);

const Groups = () => {
    const allGroupsLoading = useStore((state) => state.allGroupsLoading);
    const groupsToShow = useStore((state) => state.groupsToShow);
    const selectedCategory = usePersistStore((state) => state.selectedCategory);

    if (allGroupsLoading || !groupsToShow) return <GroupsSkeleton />;

    const responsiveWidth =
        PLATFORM === 'web'
            ? ['100vw', '100vw', 'calc(100vw - 72px)']
            : 'calc(100vw - 72px)';

    return (
        <Flex
            id="groups-container"
            m="20px 0 0"
            h="calc(100vh - 72px)"
            overflowX="auto"
            w={responsiveWidth}
            p="0 0 16px 24px"
            pos="relative"
            /* className={
                colorMode !== 'light'
                
                    ? 'groupScrollAreaDarkHover'
                    : 'groupScrollAreaHover'
            } */
        >
            <AnimatePresence>
                {groupsToShow.length ? (
                    <motion.div
                        key={selectedCategory}
                        style={{ display: 'flex', flexDirection: 'row' }}
                        initial={{ x: 16, opacity: 0 }}
                        animate={{
                            x: 0,
                            opacity: 1,
                            transition: {
                                delay: 0.18,
                                duration: 0.3,
                                opacity: { duration: 0.15, delay: 0.18 }
                            }
                        }}
                    >
                        <SortableContext
                            items={groupsToShow}
                            strategy={horizontalListSortingStrategy}
                        >
                            {groupsToShow.map((g, i) => {
                                if (g.collapsed) {
                                    return (
                                        <CollapsedSpace
                                            key={g.id}
                                            group={g}
                                            index={i}
                                        />
                                    );
                                } else {
                                    return (
                                        <ResizableGroupColumn
                                            key={g.id}
                                            groupData={g}
                                            index={i}
                                        />
                                    );
                                }
                            })}
                        </SortableContext>
                    </motion.div>
                ) : (
                    <EmptyStateContainer />
                )}
            </AnimatePresence>
            <DropTabNewGroup />
            {/* {groupsToShow.length > 3 && <FadeOutEndButton />} */}
        </Flex>
    );
};

export default Groups;

export function scrollToGroupContainer(scrollTo) {
    const element = document.getElementById('groups-container');
    if (element) {
        if (scrollTo === 'end') {
            element.scrollLeft = element.scrollWidth + 1000;
        } else if (scrollTo === 'start') {
            element.scrollLeft = 0;
        }
    }
}

const ResizableGroupColumn = ({ groupData, index }) => {
    const workspaceZoom = layoutStore((state) => state.workspaceZoom);

    const reSizeW = groupData.customWidth ? groupData.customWidth : 367;
    const [width, setWidth] = useStateCallback(reSizeW);
    useEffect(() => {
        if (width !== reSizeW) {
            setWidth(reSizeW);
        }
    }, [reSizeW]);
    const sendToDb = (newWidth) => {
        useStore.getState().changeSpaceWidth(groupData.id, newWidth);
    };

    return (
        <Box h="100%" mr="16px" my="auto" display="block">
            <Resizable
                size={{ width: width, height: '100%' }}
                minWidth={'240px'}
                //maxWidth={'calc(100vw - 84px)'}
                enable={{
                    top: false,
                    right: true,
                    bottom: false,
                    left: false,
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false
                }}
                onResizeStop={(e, direction, ref, d) => {
                    if (d.width === 0 && d.height === 0) {
                        //handleDoubleClick(e);
                    } else {
                        let newWidth = width + d.width;
                        setWidth(newWidth, (newWidth) => sendToDb(newWidth));
                    }
                    //setWidth(width + d.width);
                }}
                //style={{ overflow: 'hidden' }}
            >
                {workspaceZoom === 'zoomed-in' && groupData.isStacked ? (
                    <StackedGroups
                        groupData={groupData}
                        index={index}
                        width={width}
                    />
                ) : workspaceZoom === 'zoomed-in' ? (
                    <GroupBox
                        groupData={groupData}
                        index={index}
                        width={width}
                    />
                ) : null}
            </Resizable>
        </Box>
    );
};

export const StackedGroups = ({ groupData, index, width }) => {
    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            {groupData.stackedGroups.map((g, i) => (
                <GroupBox key={i} groupData={g} index={index} width={width} />
            ))}
        </div>
    );
};

export const GroupBox = ({ groupData, index, width }) => {
    const groupRef = useRef(null);
    const [groupHeight, setGroupHeight] = useState(0);
    const [groupIsDragging, setGroupIsDragging] = useState(false);

    useEffect(() => {
        if (groupRef.current) {
            setGroupHeight(groupRef.current.offsetHeight);
        }
    }, []);
    const insertPosition = useDndStore(
        (state) => state.insertPosition[groupData.id]
    );
    const isDraggingOver = useDndStore(
        (state) => state.overContainerZu === groupData.id
    );

    const customShadows = {
        left: 'inset 4px 0 0px -2px #44e18c',
        right: 'inset -4px 0 0px -2px #44e18c',
        over: 'inset 0 4px 0px -2px #44e18c',
        under: 'inset 0 -4px 0px -2px #44e18c',
        center: 'inset 0 0 0 2px #44e18c',
        emptyContainer: 'inset 0 0 0 2px #44e18c'
    };

    const spaceShadow = useColorModeValue(
        '0 24px 24px -30px rgba(165,182,185,0.42), 0 6px 12px -4px rgba(171,174,181,0.36)',
        '0 24px 24px -30px rgba(0,0,0,0.50), 0 6px 12px -4px rgba(0,0,0,0.38)'
    );

    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: groupData.id,
        data: {
            type: 'dragGroup',
            data: { ...groupData, groupHeight: 'calc(100vh - 105px)' },
            width: width + 'px',
            oIndex: index
        }
    });

    /* insertPosition
        ? customShadows[insertPosition] || spaceShadow
        :  */
    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };
    const handleNodeRef = (node) => {
        // (ref as React.MutableRefObject<HTMLDivElement>).current = node;
        groupRef.current = node;
        setNodeRef(node);
    };

    const boxShadow =
        insertPosition === 'center' || isDraggingOver
            ? customShadows['center']
            : spaceShadow;
    //const dropBgColor = useColorModeValue('#d2e3ff', '#2a2b2c'); //'#073019' //lightgray:  #e9ecf1
    /* const bgDynamic =
        insertPosition === 'center' || isDraggingOver ? dropBgColor : 'groupBg'; */

    return (
        // <GroupDroppable id={groupData.id}>
        <Box
            ref={(node) => handleNodeRef(node)}
            boxShadow={boxShadow}
            rounded="18px"
            h="100%"
            // mr="16px"
            bg="groupBg"
            my="auto"
            px="0px"
            display="block"
            style={style}
            opacity={isDragging ? 0.5 : ''}
            // opacity={groupIsDragging ? 0.5 : ''}
            {...attributes}
        >
            <ErrorBoundary
                FallbackComponent={GroupErrorFallback}
                onReset={() => {}}
            >
                <Header
                    groupData={groupData}
                    howManySites={groupData.tabs?.length}
                    index={index}
                    width={width}
                    groupHeight={groupHeight}
                    setGroupIsDragging={setGroupIsDragging}
                    listeners={listeners}
                />
                <GroupContent
                    tabs={groupData.tabs}
                    groupId={groupData.id}
                    width={width}
                />
            </ErrorBoundary>
        </Box>
        // <GroupInsertLine side={insertPosition} />
        // </GroupDroppable>
    );
};

export const GroupInsertLine = ({ side }) => {
    const isLeft = side === 'left';
    const isRight = side === 'right';

    return (
        <>
            {isLeft && (
                <motion.div
                    initial={{ scaleY: 0.875 }}
                    animate={{ scaleY: 1 }}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: -9,
                        borderRadius: '3px',
                        height: '100%',
                        width: '2px',
                        backgroundColor: '#44e18c'
                    }}
                />
            )}
            {isRight && (
                <motion.div
                    initial={{ scaleY: 0.875 }}
                    animate={{ scaleY: 1 }}
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: -9,
                        borderRadius: '3px',
                        height: '100%',
                        width: '2px',
                        backgroundColor: '#44e18c'
                    }}
                />
            )}
        </>
    );
};

export const InsertIndicatorLine = ({ side }) => {
    const isOver = side === 'over';
    const isUnder = side === 'under';
    const isLeft = side === 'left';
    const isRight = side === 'right';

    return (
        <Box position="relative">
            {isOver && (
                <motion.div
                    style={{
                        position: 'absolute',
                        top: '-50px', // Adjust as needed
                        left: 0,
                        right: 0,
                        height: '2px', // Line thickness
                        width: '100%',
                        backgroundColor: 'blue' // Line color
                    }}
                />
            )}
            {isUnder && (
                <motion.div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px', // Line thickness
                        width: '100%',
                        backgroundColor: 'blue' // LiLine color
                    }}
                />
            )}
            {isLeft && (
                <motion.div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px', // Line thickness
                        width: '100%',
                        backgroundColor: 'blue' // LiLine color
                    }}
                />
            )}
            {isRight && (
                <motion.div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px', // Line thickness
                        width: '100%',
                        backgroundColor: 'blue' // LiLine color
                    }}
                />
            )}
        </Box>
    );
};

export const Header = ({
    groupData,
    howManySites,
    overlayItem = false,
    index,
    width,
    groupHeight,
    setGroupIsDragging = (state) => {},
    listeners = null
}) => {
    const { emoji, title, id } = groupData;
    const trackColor = useColorModeValue('gray.100', '#35393c');
    const onSubmitTitle = (newTitle) => {
        useStore.getState().updateGroup({ title: newTitle }, id);
        layoutStore.getState().setNewlyAddedGroup([]);
    };
    /* const globalGroupIsDragging = useDndStore(
        (state) => state.globalGroupIsDragging
    ); */

    // const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    //     id: id,
    //     data: {
    //         type: 'dragGroup',
    //         data: { ...groupData, groupHeight: groupHeight },
    //         width: width,
    //         oIndex: index
    //     }
    // });

    // useEffect(() => {
    //     if (globalGroupIsDragging && isDragging) {
    //         setGroupIsDragging(true);
    //     } else {
    //         setGroupIsDragging(false);
    //     }
    // }, [globalGroupIsDragging, isDragging]);

    // const handlers = overlayItem ? {} : { ...attributes, ...listeners };

    //Could be put into useMemo for performance if needed ->

    const newlyCreated = layoutStore.getState().newlyAddedGroup.includes(id);
    const groupIsLoading = useStore(
        (state) => state.loadingGroups[groupData.id]
    );

    return (
        <Flex
            id={id + 'dropTarget'}
            // ref={overlayItem ? null : setNodeRef}
            {...listeners}
            p="18px 16px 12px"
            alignItems="center"
            role="group"
            cursor={overlayItem ? 'grabbing' : 'grab'}
            style={{
                outline: 'none' //used to disable dnd focus
            }}
            overflow={'hidden'}
        >
            <div onMouseDown={(e) => e.stopPropagation()}>
                <EmojiPicker emoji={emoji} type={'group'} id={id} />
            </div>
            <Flex
                flexDir={'column'}
                ml="10px"
                mt="0px"
                maxW="calc(100% - 106px)"
            >
                <div onMouseDown={(e) => e.stopPropagation()}>
                    <EditableTitle
                        size={'groupTitle'}
                        title={title}
                        onSubmit={onSubmitTitle}
                        focusInput={newlyCreated}
                    />
                </div>
                <OpenGroupButton groupData={groupData} />
            </Flex>
            {groupIsLoading ? (
                <Spinner
                    size="xs"
                    color="blue.300"
                    emptyColor={trackColor}
                    mr="10px"
                    ml="auto"
                    mb="auto"
                    mt="10px"
                />
            ) : (
                !overlayItem && (
                    <GroupMenu groupId={groupData.id} index={index} />
                )
            )}
        </Flex>
    );
};

/* const SpinnerAndCheck = ({ groupIsLoading }) => {
    const [showCheckmark, setShowCheckmark] = useState(false);
    const trackColor = useColorModeValue('gray.100', '#35393c');

    useEffect(() => {
        if (!groupIsLoading && showCheckmark === false) {
            setShowCheckmark(true);
            setTimeout(() => setShowCheckmark(false), 400);
        }
    }, [groupIsLoading]);

    return (
        <AnimatePresence mode="popLayout" initial={false}>
            {groupIsLoading ? (
                <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                        // margin: '4px 8px auto auto'
                        position: 'absolute',
                        right: '24px',
                        top: '22px'
                    }}
                >
                    <Spinner
                        size="xs"
                        color="blue.300"
                        emptyColor={trackColor}
                    />
                </motion.div>
            ) : (
                showCheckmark && (
                    <motion.div
                        key="done"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: 'absolute',
                            right: '19px',
                            top: '22px'
                        }}
                    >
                        <CheckCircle size="19px" />
                    </motion.div>
                )
            )}
        </AnimatePresence>
    );
}; */

const GroupContent = ({ tabs, groupId, width }) => {
    const [hoverList, hover] = useBoolean();
    const [blockNewNote, setNoteBlock] = useState(false);

    // Calculate the width as before
    let itemWidth = (width - 25 - 0.0225 * width) * 1.0225;

    return (
        <Box
            onMouseEnter={hover.on}
            onMouseOver={hover.on}
            onMouseLeave={hover.off}
            style={{
                height: 'calc(100% - 94px)',
                overflow: 'auto'
                //padding: '0px 6px 8px 12px'
            }}
            className="groupScrollArea"
            // Assuming `slimScrollStyles` is a valid object you've defined elsewhere
            sx={slimScrollStyles}
        >
            <Virtuoso
                data={tabs}
                style={{
                    height: '100%',
                    width: '100%',
                    //padding: '0px 6px 8px 12px',
                    //overflowX: 'hidden',
                    overflowY: 'auto'
                    //scrollbarGutter: 'stable'
                }} // Ensure the Virtuoso container fills the parent
                className="groupScrollArea"
                itemContent={(index, tab) => (
                    <DndItem
                        key={tab.id} // Key is not necessary here, Virtuoso handles it
                        data={tab}
                        groupId={groupId}
                        width={`${itemWidth}px`}
                    />
                )}
                // overscan={120}
                components={{
                    Footer: () => (
                        <NewNoteTapArea
                            hoverList={hoverList}
                            groupId={groupId}
                            emptyGroup={tabs.length === 0}
                            blockNewNote={blockNewNote}
                            setNoteBlock={setNoteBlock}
                        />
                    )
                }}
            />
        </Box>
    );
};

const FadeOutEndButton = () => {
    const fadeOutBg = useColorModeValue(
        'linear-gradient(90deg, rgba(240,241,242,0.00) 0%, #F0F1F2 100%)',
        'linear-gradient(90deg, rgba(21,23,27,0.00) 0%, #18191b 100%)'
    );
    const [isHovered, hover] = useBoolean();

    return (
        <Box
            h="calc(100vh - 72px)"
            flex="1"
            w="48px"
            pos="fixed"
            right="0px"
            opacity={0.5}
            backgroundImage={fadeOutBg}
            zIndex={3}
            onMouseEnter={hover.on}
            onMouseOver={hover.on}
            onMouseLeave={hover.off}
            p="8px 4px"
            mb="16px"
        >
            {isHovered && (
                <MotionBox
                    h="calc(100% - 32px)"
                    w="100%"
                    bg="rgba(255,255,255,0.14)"
                    rounded="8px"
                    initial={{
                        opacity: 0
                    }}
                    animate={{
                        opacity: 1,
                        transition: { delay: 0.12, duration: 0.6 }
                    }}
                    display="flex"
                    alignContent={'center'}
                    justifyContent={'center'}
                    cursor={'pointer'}
                    whileTap={{ scale: 0.975 }}
                >
                    <ChevronRightIcon m="auto" size="24px" />
                </MotionBox>
            )}
        </Box>
    );
};

const EmptyStateContainer = () => {
    const { colorMode } = useColorMode();
    const emptyStateText = useColorModeValue('gray.600', '#FFF');
    const [loaded, setLoaded] = useState(false);
    const hoverSide = layoutStore((state) => state.hoverSide);

    const globalIsDragging = useDndStore((state) => state.globalIsDragging);

    if (globalIsDragging) return null;

    return (
        <Flex
            pos="absolute"
            //center this in the middle of the parent
            top="calc(50% - 199px)"
            left="calc(50% - 169px)"
            flexDir="column"
            m="auto"
            alignItems="center"
            pb="84px"
            pr="93px"
            pointerEvents="none"
            userSelect="none"
            /* transform={hoverSide ? 'scale(0.94) translateX(-184px)' : ''} */
            transition="transform 0.14s cubic-bezier(0.77,0,0.18,1)"
        >
            <Box>
                <Image
                    onLoad={() => setLoaded(true)}
                    visibility={loaded ? 'visible' : 'hidden'}
                    src={
                        colorMode === 'light'
                            ? '../../assets/images/EmptyState_Spaces.png'
                            : '../../assets/images/EmptyState_SpacesDark.png'
                    }
                    boxSize="245px"
                    minW="245px"
                    mb="24px"
                    borderStyle="none"
                    border="0px"
                    borderColor="transparent"
                    pointerEvents="none"
                    userSelect="none"
                />
            </Box>
            <Text
                /* initial={{filter: "blur(4px)", opacity: "0", scale: 1.25}} animate={{filter: "blur(0px)", opacity: "0.4", scale: 1}} transition={{ duration: 1.2 }} */ m="auto"
                fontSize="14px"
                color={emptyStateText}
                letterSpacing="1.24px"
                textAlign="center"
                fontWeight="600"
                opacity="0.5"
                pointerEvents="none"
                userSelect="none"
            >
                Empty category
            </Text>
            <Text
                mt="8px"
                fontSize="12px"
                color={emptyStateText}
                textAlign="center"
                fontWeight="medium"
                opacity="0.4"
                pointerEvents="none"
                userSelect="none"
            >
                {PLATFORM === 'web'
                    ? 'Create group from dots menu(Ctrl/⌘ + G)'
                    : 'Drag and drop tab for new group(Ctrl/⌘ + G)'}
            </Text>
        </Flex>
    );
};

// const ItemMiddleTest = ({ data, groupId }) => {
//     const insertPosition = useDndStore(
//         (state) => state.insertPosition[data.id]
//     );
//     const activeItem = useDndStore((state) => state.activeItem);

//     return (
//         <>
//             <motion.div
//                 layout
//                 transition={{ type: 'spring', stiffness: 300, damping: 30 }}
//             >
//                 {insertPosition === 'over' && (
//                     <ItemPresentation
//                         data={activeItem}
//                         groupId={groupId}
//                         overlayItem={true}
//                     />
//                 )}
//                 <Item data={data} groupId={groupId} />
//                 {insertPosition === 'under' && (
//                     <ItemPresentation
//                         data={activeItem}
//                         groupId={groupId}
//                         overlayItem={true}
//                     />
//                 )}
//             </motion.div>
//         </>
//     );
// };

function useStateCallback(initialState) {
    const [state, setState] = useState(initialState);
    const cbRef = useRef(null); // mutable ref to store current callback
    const setStateCallback = (state, cb) => {
        cbRef.current = cb; // store passed callback to ref
        setState(state);
    };
    useEffect(() => {
        // cb.current is `null` on initial render, so we only execute cb on state *updates*
        if (cbRef.current) {
            cbRef.current(state);
            cbRef.current = null; // reset callback after execution
        }
    }, [state]);

    return [state, setStateCallback];
}

const NewNoteTapArea = ({
    hoverList,
    groupId,
    emptyGroup,
    blockNewNote,
    setNoteBlock
}) => {
    const globalIsDragging = useDndStore((state) => state.globalIsDragging);
    const createNoteAction = useStore((state) => state.createNote);
    const noteEditMode = layoutStore((state) => state.noteEditMode);
    const commentBgColor = useColorModeValue('gray.100', '#35393c');

    const createNoteInner = () => {
        if (isTrialReached()) {
            return;
        }
        setNoteBlock(true);
        createNoteAction(groupId);
    };

    useEffect(() => {
        return () => {
            setNoteBlock(false);
        };
    }, []);

    const { newNoteMt, newNoteHeight } = useItemSize();

    return (
        <div style={{ minHeight: '46px', marginTop: newNoteMt }}>
            <AnimatePresence>
                {hoverList &&
                    !globalIsDragging &&
                    !noteEditMode &&
                    !blockNewNote && (
                        <MotionBox
                            onClick={createNoteInner}
                            mx="22px"
                            h={newNoteHeight}
                            flex="1"
                            //w="calc(100% - 40px)"
                            //mt="10px"
                            cursor="text"
                            bg={commentBgColor}
                            rounded="7px"
                            opacity="0.65"
                            display="flex"
                            initial={{
                                scale: 0.5,
                                transformOrigin: emptyGroup
                                    ? '0% 0%'
                                    : '100% 100%'
                            }}
                            animate={{
                                scale: 1,
                                transformOrigin: emptyGroup
                                    ? '0% 0%'
                                    : '100% 100%',
                                transition: {
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 24
                                }
                            }}
                            whileHover={{ opacity: 1 }}
                            whileTap={{
                                scale: 0.9925,
                                transition: { duration: 0.1 }
                            }}
                        />
                    )}
            </AnimatePresence>
        </div>
    );
};

export const CollapsedSpace = ({ group, index, overlayItem = false }) => {
    const maximizeGroup = () => {
        useStore.getState().toggleMinimizeGroup(group.id, false);
    };
    /* const globalGroupIsDragging = useDndStore(
        (state) => state.globalGroupIsDragging
    ); */
    const insertPosition = useDndStore(
        (state) => state.insertPosition[group.id]
    );
    const isDraggingOver = useDndStore(
        (state) => state.overContainerZu === group.id
    );

    const spaceShadow = useColorModeValue(
        'inset 0 0 8px 0px #E2E8F0,inset 0 0 1px 1px #E2E8F0',
        'inset 0 0 8px 0px #262A2D,inset 0 0 1px 1px #262A2D'
    );
    const boxShadow =
        insertPosition === 'center' || isDraggingOver
            ? 'inset 0 0 0 2px #44e18c'
            : spaceShadow;

    const icon = useColorModeValue('gray.600', '#A1A2A4');
    const urlText = useColorModeValue('gray.400', '#4b4d51');
    const hoverButton = useColorModeValue('gray.100', '#32363A');

    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: group.id,
        data: {
            type: 'dragGroup',
            collapsedGroup: true,
            data: { ...group, groupHeight: 'calc(100vh - 105px)' },
            width: '48px',
            oIndex: index
        }
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    return (
        // <GroupDroppable id={group.id}>
        <Box
            id={group.id + 'dropTarget'}
            ref={setNodeRef}
            w="48px"
            display="flex"
            flexDir="column"
            h="100%"
            boxShadow={boxShadow}
            role="group"
            style={{ cursor: 'pointer', ...style }}
            pos="relative"
            mr="14px"
            rounded="12px"
            bg="groupBg"
            minH="96px"
            cursor={'grab'}
            opacity={isDragging ? 0.5 : ''}
            onMouseEnter={handleMouseEnter} // Add mouse enter handler
            onMouseLeave={handleMouseLeave} // Add mouse leave handler
            {...attributes}
            {...listeners}
        >
            <Box
                transform={
                    isHovered && !overlayItem
                        ? 'translateY(12px)'
                        : 'translateY(0px)'
                }
                opacity={isHovered && !overlayItem ? '0' : '1'}
                transition="0.12s ease-in-out"
                display="flex"
                h="36px"
                w="36px"
                pos="absolute"
                top="8px"
                left="6px"
            >
                {group.emoji.length > 10 ? (
                    <Image
                        src={group.emoji}
                        overflow="hidden"
                        rounded="4px"
                        height="30px"
                        width="30px"
                        m="auto"
                    />
                ) : (
                    <Text fontSize="24px" m="auto">
                        {group.emoji}
                    </Text>
                )}
            </Box>
            {true && (
                <Box
                    transform={
                        isHovered && !overlayItem
                            ? 'translateY(0px)'
                            : 'translateY(-8px)'
                    }
                    opacity={isHovered && !overlayItem ? '1' : '0'}
                    transition="0.12s ease-in-out"
                    display="flex"
                    h="36px"
                    w="36px"
                    pos="absolute"
                    top="8px"
                    left="6px"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <IconButton
                        icon={<ChevronOpen />}
                        aria-label="Maximize row"
                        h="36px"
                        minW="36px"
                        color={icon}
                        bg={hoverButton}
                        //_hover={{ bg: hoverButton }}
                        rounded="9px"
                        fontSize="16px"
                        /* pl="4px"
                    pr="5px" */
                        pt="6px"
                        //pt="8px"
                        // cursor="pointer"
                        onClick={maximizeGroup}
                    />
                </Box>
            )}

            <Text
                mt="64px"
                fontSize="20px"
                fontWeight="600"
                style={{ transform: 'rotate(90deg)', whiteSpace: 'nowrap' }}
                color={urlText}
                letterSpacing="0.84px"
                opacity={0.8}
            >
                {group.title.length ? group.title : 'Untitled'}
            </Text>
        </Box>
        // </GroupDroppable>
    );
};
